<template>
  <v-card elevation="0" rounded="lg">
    <v-row class="new-employee-page">
      <v-col cols="12" class="ga-5 d-flex flex-column">
        <p class="text-h6 pl-4 pt-2 text-grey-darken-3">{{ t('employees.completeDoctorInfo') }}</p>
      </v-col>
    </v-row>
    <v-col cols="12">
      <div class="d-flex align-center ga-3 pa-5 bg-tonal rounded-lg ">
        <div class="pa-3 bg-primary rounded-lg">
          <v-icon class="text-white" size="20">
            $userOutlined
          </v-icon>
        </div>
        <h5 class="text-primary text-h5">
          {{ t('employees.employeeInfo') }}
        </h5>
      </div>
    </v-col>
    <v-col cols="12" class="py-2">
      <v-tabs v-model="tab" bg-color="employeeInfo">
        <v-tab value="personalData">{{ t("employees.personalData") }}</v-tab>
        <v-tab value="schedules" :disabled="!employeeCreated">{{ t("menuItems.schedules") }}</v-tab>
        <v-tab value="services" :disabled="!employeeCreated">{{ t("menuItems.services") }}</v-tab>
      </v-tabs>
    </v-col>
    <v-card-text class="pa-1 pa-sm-2 pa-lg-4">
      <v-tabs-window v-model="tab">
        <v-tabs-window-item value="personalData">
          <EmployeePersonalData ref="personalDataRef" :loading="isCreatingEmployee" :error="creationError"
            @personal-data-updated="handlePersonalDataUpdate" @validation-changed="handleValidationChange"
            @save="handlePersonalDataSave" />
        </v-tabs-window-item>
        <v-tabs-window-item value="schedules">
          <div v-if="!employeeCreated" class="text-center pa-8">
            <v-icon size="48" color="grey-lighten-1" class="mb-4">mdi-clock-outline</v-icon>
            <p class="text-h6 text-grey-lighten-1 mb-2">{{ t('employees.scheduleNotAvailable') }}</p>
            <p class="text-body-2 text-grey">{{ t('employees.completePersonalDataFirst') }}</p>
          </div>
          <div v-else>
            <EmployeeScheduleData :model-value="false" :company-uuid="company?.uuid || ''"
              :employee-uuid="createdEmployeeUuid || undefined" :schedules="[]"
              @schedules-updated="handleSchedulesUpdated" @save-success="handleScheduleSuccess"
              @save-error="handleScheduleError" />
            <EmployeeCreationNavigation
              :selected-count="0"
              @continue="handleSchedulesContinue"
              @skip="handleSchedulesSkip"
            />
          </div>
        </v-tabs-window-item>
        <v-tabs-window-item value="services">
          <div v-if="!employeeCreated" class="text-center pa-8">
            <v-icon size="48" color="grey-lighten-1" class="mb-4">$service</v-icon>
            <p class="text-h6 text-grey-lighten-1 mb-2">{{ t('employees.servicesNotAvailable') }}</p>
            <p class="text-body-2 text-grey">{{ t('employees.completePersonalDataFirst') }}</p>
          </div>
          <div v-else>
            <ServiceManagement 
              :employee="createdEmployee"
              :company-uuid="company?.uuid || ''"
              :show-actions="false"
              @services-changed="handleServicesChanged"
            />
            <EmployeeCreationNavigation
              :selected-count="selectedServiceIds.length"
              :is-final-step="true"
              @continue="handleServicesContinue"
              @skip="handleServicesSkip"
            />
          </div>
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick } from 'vue'
import dayjs from 'dayjs'
import { useCompanyStore } from '~/stores/company'
import { useSnackbarStore } from '~/stores/snackbar'
import type { Company } from '~/interfaces/companyInterface'
import EmployeePersonalData from '~/components/employees/EmployeePersonalData.vue';
import EmployeeScheduleData from '~/components/employees/EmployeeScheduleData.vue';
import { useEmployeesComposable } from '~/composables/api/employee'
import ServiceManagement from '~/components/services/ServiceManagement.vue'
import EmployeeCreationNavigation from '~/components/employees/EmployeeCreationNavigation.vue'
import type { Employee } from '~/interfaces/employeeInterface'

definePageMeta({
  layout: 'dashboard-layout',
  middleware: 'loggedin'
})

const companyStore = useCompanyStore()
const company = computed(() => companyStore.company as Company)

const emit = defineEmits(['set-title'])
const { t } = useI18n()

const tab = ref('personalData')
const createdEmployeeUuid = ref<string | null>(null)
const employeeCreated = ref(false)
const personalDataRef = ref()
const createdEmployee = ref<Employee | null>(null)
const selectedServiceIds = ref<string[]>([])

// Mirrors EmployeePersonalData.vue's emitted EmployeeFormData exactly: that
// component always emits every field as a (possibly empty) string, never
// undefined, so this side was exaggerating optionality relative to the real
// contract. Kept as a local type (rather than importing the child's) since
// the field names/shape are what matter for the API payload built below.
interface EmployeeAddressFormData {
  postalCode: string
  city: string
  street: string
  streetNumber: string
  doorNumber: string
}

interface EmployeePersonalFormData {
  title: string
  first_name: string
  last_name: string
  phone: string
  email: string
  address: EmployeeAddressFormData
  document_type: string
  document_number: string
  birthdate: string | null
  isUser: boolean
}

const employeeData = ref<{
  personalData: EmployeePersonalFormData | null,
  scheduleData: unknown
}>({
  personalData: null,
  scheduleData: null
})

const isPersonalDataValid = ref(false)
const isCreatingEmployee = ref(false)
const creationError = ref('')

const handlePersonalDataUpdate = (data: EmployeePersonalFormData) => {
  employeeData.value.personalData = data
}

const handlePersonalDataSave = async () => {
  if (isPersonalDataValid.value && employeeData.value.personalData) {
    await createEmployee()
  }
}

const handleValidationChange = (isValid: boolean) => {
  isPersonalDataValid.value = isValid
}

const handleSchedulesUpdated = (_schedules: unknown) => {
  // Schedules updated
}

const handleServicesChanged = (serviceIds: string[]) => {
  selectedServiceIds.value = serviceIds
}

const handleSchedulesContinue = () => {
  tab.value = 'services'
}

const handleSchedulesSkip = () => {
  tab.value = 'services'
}

const handleServicesContinue = () => {
  // This is the final step, so we complete the flow
  handleFinalCompletion('Empleado creado exitosamente con servicios asignados')
}

const handleServicesSkip = () => {
  // Skip services and finish
  handleFinalCompletion('Empleado creado exitosamente')
}

const handleScheduleSuccess = async (message: string) => {
  // This is called when schedules are saved, now move to services
  useSnackbarStore().setSnackbar(message, 'success')
  tab.value = 'services'
}

const handleFinalCompletion = async (message: string) => {
  // If we have selected services, update the employee with them first
  if (selectedServiceIds.value.length > 0 && createdEmployee.value) {
    try {
      const { updateEmployee } = useEmployeesComposable()
      await updateEmployee(
        company.value.uuid,
        createdEmployee.value.uuid,
        createdEmployee.value.address!,
        createdEmployee.value.status,
        selectedServiceIds.value,
        false
      )
    } catch (error) {
      console.error('Error updating employee services:', error)
      // Continue with navigation even if services update fails
    }
  }

  useSnackbarStore().setSnackbar(message, 'success')

  const router = useRouter()
  if (createdEmployeeUuid.value) {
    await router.push(`/company/${company.value.uuid}/employees/${createdEmployeeUuid.value}`)
  } else {
    await router.push(`/company/${company.value.uuid}/employees`)
  }
}

const handleScheduleError = (error: string) => {
  useSnackbarStore().setSnackbar(error, 'error')
}

const createEmployee = async () => {
  const personalData = employeeData.value.personalData
  let formattedBirthdate = personalData?.birthdate ?? null

  if (formattedBirthdate && typeof formattedBirthdate === 'string') {
    try {
      formattedBirthdate = dayjs(formattedBirthdate).format('DD/MM/YYYY')
    } catch (e) {
      console.error('Error formatting birthdate:', e)
      formattedBirthdate = personalData?.birthdate ?? null
    }
  } else {
    formattedBirthdate = null
  }

  if (!personalData) {
    return
  }

  isCreatingEmployee.value = true
  creationError.value = ''

  const apiData = {
    address: {
      title: personalData.title,
      first_name: personalData.first_name,
      last_name: personalData.last_name,
      name: `${personalData.first_name} ${personalData.last_name}`,
      contact: personalData.phone,
      phone: personalData.phone || '',
      email: personalData.email,
      postal_code: personalData.address?.postalCode || '',
      city: personalData.address?.city || '',
      street: personalData.address?.street || '',
      street_number: String(parseInt(personalData.address?.streetNumber, 10) || 0),
      extended_address: personalData.address?.doorNumber || '',
      document_type: personalData.document_type,
      document_number: personalData.document_number,
      birthdate: formattedBirthdate
    },
    status: 'active' as const,
    all_services: false,
    services: [] as string[],
  }

  try {
    const { createEmployee: createEmployeeApi, updateEmployeeUserStatus } = useEmployeesComposable()
    const result = await createEmployeeApi(company.value.uuid, apiData) as Record<string, unknown>

    if (result.error) {
      creationError.value = t('employees.errors.createFailed')
    } else {
      employeeCreated.value = true

      const employeeData = (result.data as Record<string, unknown> | undefined) || result
      const employeeUuid = (employeeData?.uuid as string | undefined)
        || ((employeeData?.value as Record<string, unknown> | undefined)?.uuid as string | undefined)
        || ((employeeData?.data as Record<string, unknown> | undefined)?.uuid as string | undefined)

      if (employeeUuid) {
        createdEmployeeUuid.value = employeeUuid
        
        // Create the employee object for the services tab
        createdEmployee.value = {
          uuid: employeeUuid,
          created_at: new Date().toISOString(),
          address: {
            title: personalData.title,
            first_name: personalData.first_name,
            last_name: personalData.last_name,
            contact: personalData.phone,
            phone: personalData.phone || '',
            email: personalData.email,
            postal_code: personalData.address?.postalCode || '',
            city: personalData.address?.city || '',
            street: personalData.address?.street || '',
            street_number: String(parseInt(personalData.address?.streetNumber, 10) || 0),
            extended_address: personalData.address?.doorNumber || '',
            document_type: personalData.document_type,
            document_number: personalData.document_number,
            birthdate: formattedBirthdate,
            name: `${personalData.first_name} ${personalData.last_name}`
          },
          status: 'active' as const,
          services: [],
          user: null
        }
        
        if (personalDataRef.value?.uploadAvatarIfSelected) {
          await personalDataRef.value.uploadAvatarIfSelected(company.value.uuid, employeeUuid)
        }

        // Handle user account creation if isUser is true
        if (personalData.isUser) {
          try {
            const userResult = await updateEmployeeUserStatus(
              company.value.uuid,
              employeeUuid,
              true
            )
            if (userResult) {
              useSnackbarStore().setSnackbar(
                t('employees.employeeAndUserCreated'),
                'success'
              )
            } else {
              console.error('❌ User account creation failed - no response:', userResult)
              // Still show success for employee creation but warn about user account
              useSnackbarStore().setSnackbar(
                t('employees.employeeCreatedButUserFailed'),
                'warning'
              )
            }
          } catch (userError) {
            console.error('❌ Error creating user account:', userError)
            // Still show success for employee creation but warn about user account
            useSnackbarStore().setSnackbar(
              t('employees.employeeCreatedButUserFailed'),
              'warning'
            )
          }
        } else {
          // Regular employee creation success
          useSnackbarStore().setSnackbar(
            t('employees.employeeCreated'),
            'success'
          )
        }
      } else {
        creationError.value = t('employees.errors.scheduleFailed')
      }

      await nextTick()
      tab.value = 'schedules'
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    console.error('Employee creation error:', errorMessage)
    creationError.value = `${t('employees.errors.unexpected')}: ${errorMessage}`
  } finally {
    isCreatingEmployee.value = false
  }
}

emit('set-title', 'Nuevo Trabajador')

</script>